import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  MoreOutlined,
  TagsOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { faDrone, faUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dropdown, List, Menu, Space, Typography } from "antd";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { graphql, useFragment } from "react-relay";

import { Tooltip } from "@skydio/rivet-ui";

import DockIcon, { G47Icon } from "components/DockIcon";
import { useFlightLogUpload } from "hooks/mutation/uploadFlightLogs";
import { useSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useFlightDuration } from "hooks/useFlightDuration";
import { useReverseGeocode } from "hooks/useReverseGeocode";
import { canUserAuditOtherOrgs } from "state/current_user/selectors";
import { formatPilotName } from "utils/common";
import { displayMutationResults } from "utils/mutations";
import { trackUserAction } from "utils/trackEvent";

import FlightAnnotations from "./components/FlightAnnotations";

import styles from "./FlightListItem.module.scss";

import type { FC } from "react";
import type { FlightListItem_flight$key } from "__generated__/FlightListItem_flight.graphql";

interface FlightMetaDataItemProps {
  icon: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  truncate?: boolean;
}

export const FlightMetaDataItem: React.FC<FlightMetaDataItemProps> = ({
  icon,
  children,
  className,
  truncate,
}) => {
  return (
    <Typography.Text type="secondary" className={"flex space-x-2 " + className}>
      <div className="flex-none">{icon}</div>
      <div className={classNames("flex-auto", { truncate })}>{children}</div>
    </Typography.Text>
  );
};

export interface Props {
  flightRef: FlightListItem_flight$key;
  selected?: boolean;
  // Pass through the currently paired dock id to determine what device data to show here
  pairedDockId?: string;
  onClick: () => void;
}

const LOGDASH_TAKEOFF_CLIENT_TYPES = new Set(["WEB", "UNKNOWN"]);

const FlightListItem: FC<Props> = ({ flightRef, selected, onClick, pairedDockId }) => {
  const { t } = useTranslation(["flights", "common"]);
  const [scrolledTo, setScrolledTo] = useState<boolean>(false);
  const automaticUploadEnabled = useFeatureFlag("automaticFileUploadPage");
  const isSkydioAdmin = useSelector(canUserAuditOtherOrgs);

  const flight = useFragment(
    graphql`
      fragment FlightListItem_flight on Flight {
        ...FlightAnnotations_flight
        id
        uuid
        ...useFlightDuration_fragment
        hasTelemetry
        takeoffClientType
        user {
          id
          email
          displayName
        }
        takeoffLocation {
          latitude
          longitude
        }
        landingLocation {
          latitude
          longitude
        }
        vehicle {
          id
          isOnline
          isOnLte
          vehicleId
          displayName
        }
        dock {
          id
          dockId
          dockType
          displayName
        }
      }
    `,
    flightRef
  );

  const logdashEnabled =
    isSkydioAdmin &&
    LOGDASH_TAKEOFF_CLIENT_TYPES.has(flight.takeoffClientType) &&
    flight.vehicle?.isOnline;
  const hasTakeOffLatLng =
    flight.takeoffLocation && flight.takeoffLocation.latitude && flight.takeoffLocation.longitude;
  const hasLandingLatLng =
    flight.landingLocation && flight.landingLocation.latitude && flight.landingLocation.longitude;
  // Use landing location as a fallback if no takeoff location or it takeoff location is [0, 0]
  const memoizedLatLng: [number, number] = useMemo(() => {
    return hasTakeOffLatLng
      ? [flight.takeoffLocation.latitude, flight.takeoffLocation.longitude]
      : hasLandingLatLng
        ? [flight.landingLocation.latitude, flight.landingLocation.longitude]
        : [0, 0];
  }, [hasTakeOffLatLng, flight.takeoffLocation, hasLandingLatLng, flight.landingLocation]);
  const address = useReverseGeocode({ memoizedLatLng, callerId: "FlightListItem" });

  const { takeoff, duration } = useFlightDuration(flight);

  const upload = useFlightLogUpload(
    displayMutationResults(response => response.requestFlightLogUpload, {
      successMessage: () =>
        `Requested upload of Skydio support logs for ${flight.vehicle?.displayName ?? ""}`,
      onSuccess: () => trackUserAction("request_flight_log_upload", { flightId: flight.uuid }),
    })
  );

  return (
    <List.Item
      style={{ display: "block" }}
      className={classNames(styles.listItem, styles.link, { [styles.selected!]: selected })}
      onClick={onClick}
    >
      <Space direction="vertical" className="w-full">
        <div
          className={styles.spaceBetween}
          ref={ref => {
            if (selected && ref && !scrolledTo) {
              // we only trigger this once so using a timeout to wait for render cycle to settle
              setTimeout(() => {
                ref.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                setScrolledTo(true);
              }, 1000);
            }
          }}
        >
          <div className={styles.flightTime}>
            <Typography.Text>{takeoff.format(t("common:format.fullDate"))}</Typography.Text>
          </div>
          <div onClick={e => e.stopPropagation()}>
            {(automaticUploadEnabled || logdashEnabled) && (
              <Dropdown
                overlay={
                  <Menu>
                    {automaticUploadEnabled && (
                      <Menu.Item
                        key="uploadLog"
                        onClick={() => upload(flight.vehicle?.id!, flight.uuid!)}
                        disabled={!!(!flight?.vehicle?.isOnline || flight?.vehicle?.isOnLte)}
                      >
                        {!!(!flight?.vehicle?.isOnline || flight?.vehicle?.isOnLte) ? (
                          <Tooltip
                            content={
                              !flight?.vehicle?.isOnline
                                ? "Log upload is disabled when drone is offline"
                                : flight?.vehicle?.isOnLte
                                  ? "Log upload is disabled when drone is connected over cellular"
                                  : null
                            }
                            closeDelay={50}
                            offset={16}
                          >
                            <span>Upload Skydio Flight Support Log</span>
                          </Tooltip>
                        ) : (
                          <span>Upload Skydio Flight Support Log</span>
                        )}
                      </Menu.Item>
                    )}
                    {logdashEnabled && (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={`https://logdash.skydio.com/?active_tab=flight_plotter_tab_s3&flight=${flight.uuid}&file_kind=vehicleLog&panel.1.name=Teleoperation`}
                      >
                        <Menu.Item
                          key="teleoperationPanel"
                          icon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                          className="!bg-red-100 dark:!bg-red-900"
                        >
                          Open in Logdash
                        </Menu.Item>
                      </a>
                    )}
                  </Menu>
                }
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </div>
        </div>
        <Space direction="vertical" style={{ width: "100%" }}>
          <FlightMetaDataItem icon={<ClockCircleOutlined />}>
            {takeoff.format(t("common:format.time"))}
            {duration ? " for " + duration.humanize() : null}
          </FlightMetaDataItem>
          <FlightMetaDataItem icon={<EnvironmentOutlined />}>
            {flight.hasTelemetry
              ? address
                ? address
                : "No address available"
              : "No telemetry available"}
          </FlightMetaDataItem>
          <FlightMetaDataItem icon={<UserOutlined />} truncate>
            {formatPilotName(flight.user, flight.takeoffClientType, true)}
          </FlightMetaDataItem>
          {!!pairedDockId && pairedDockId === flight.dock?.dockId && (
            <FlightMetaDataItem icon={flight.dock?.dockType === "G47" ? G47Icon : <DockIcon />}>
              {flight.dock?.displayName}
            </FlightMetaDataItem>
          )}
          {!!pairedDockId && (
            <FlightMetaDataItem icon={<FontAwesomeIcon icon={faDrone} />}>
              {flight.vehicle?.displayName}
            </FlightMetaDataItem>
          )}
          {isSkydioAdmin && (
            <FlightMetaDataItem icon={<TagsOutlined />} className={styles.skydioAdminOnly}>
              <FlightAnnotations flightRef={flight} />
            </FlightMetaDataItem>
          )}
        </Space>
      </Space>
    </List.Item>
  );
};

export default FlightListItem;
